import axios, { type AxiosRequestConfig } from 'axios';

import '@api-client';
import { captureException } from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// side-effect import: registers the axios auth/redirect interceptors
import { env } from '@utils/env';

/** How a person is attached to the company in Master Data. */
export type IncorporationLinkKind =
  | 'managing_director'
  | 'director'
  | 'shareholder'
  | 'ubo'
  | 'role';

/** On what evidence the backend picked the owner. */
export type OwnerSelectionReason =
  | 'mds_sso_owner'
  | 'managing_director'
  | 'earliest_linked_person';

/** How the backend matched a legacy company to this MDS record. */
export type LegacyMatchSource = 'idempotency_key' | 'reference_num';

export type MemberProvisioningOutcome = 'granted' | 'already_member' | 'failed';

export interface IncorporationCompany {
  mdsCompanyId: string;
  legalName: string;
  tradeName: string | null;
  legalForm: string | null;
  clientNumber: number | null;
  companyCode: string | null;
  rcsNumber: string | null;
  vatNumber: string | null;
  incorporationStatus: string;
  incorporationDate: string | null;
  createdAt: string | null;
  referenceNum: string | null;
  /**
   * The legacy company behind this MDS record, or null when there is none yet.
   *
   * Non-null while `incorporationStatus` is still `incorporating` means one of two very different
   * things, and `legacyMatchedBy` says which.
   */
  legacyCompanyId: string | null;
  /**
   * `idempotency_key` — this page provisioned it, and the row is the desync the "Retry MDS status"
   * action exists for. `reference_num` — the opposite: an existing client the legacy sync mirrored
   * into MDS, which is most of what sits in `incorporating`. Nothing is created for one of those.
   */
  legacyMatchedBy: LegacyMatchSource | null;

  /**
   * The newest change across the company and its child records in Master Data.
   *
   * Only the provisioned view has a use for it — it is what says *why* a row is there — and only
   * a backend that serves that view sends it, so it is optional like everything else here.
   */
  lastActivityAt?: string | null;

  /**
   * How far into the company's Master Data history the last fill consumed, or null.
   *
   * MDS's `last_activity_at` as the fill read it, not the clock when it ran — so the two
   * timestamps are directly comparable, and equal means there is nothing to bring over.
   */
  activitySeenAt?: string | null;
  /**
   * Whether a compliance officer has approved this company's case.
   *
   * The gate on provisioning. Optional and read as `=== true`, like `alreadyMember`: a backend
   * deployed before the field sends nothing, and absent must read as "not stated" — which here
   * lands on the safe side anyway, since the action is withheld either way.
   */
  complianceApproved?: boolean;
  /**
   * Whether that verdict is unknown rather than negative — compliance could not be read.
   *
   * Reported apart from `complianceApproved` because the two send a CSM to different people: no
   * approval yet is an officer's queue, an unreachable compliance service is ours.
   */
  complianceUnavailable?: boolean;
}

/** One participant Master Data attaches to the company — the set provisioning writes into legacy. */
export interface IncorporationParticipant {
  id: string;
  name: string;
  /** `legal` is a shareholding company, which legacy also stores as a `persons` row. */
  type: 'physical' | 'legal';
  kinds: IncorporationLinkKind[];
  sharesAmount: number | null;
  /**
   * Whether this participant can be given a portal account at all.
   *
   * False for someone with no `linked_user_id` in Master Data, for one whose legacy account was
   * never linked to Keycloak, and for every corporate shareholder — a company cannot log in. They
   * are still written to legacy; they simply have nothing to attach an account to.
   */
  canHoldPortalAccount: boolean;
}

export interface IncorporationCandidate {
  personId: string;
  firstName: string;
  lastName: string;
  keycloakId: string;
  kinds: IncorporationLinkKind[];
  roleTypes: string[];
  createdAt: string | null;
  isOwner: boolean;
  /**
   * Whether legacy already holds this person as a member of the matched company.
   *
   * Only ever true for an existing client the legacy sync mirrored into MDS — and there it is
   * usually true, because they are live clients whose directors already have portal accounts. That
   * is the whole reason the field exists: "Attach people and mark in service" was promising work it
   * could not know would happen.
   *
   * Optional and read as `=== true`, like `clientRoleGranted`: this type describes what the wire may
   * carry, and a backend deployed before the field sends nothing. Absent must read as "not stated",
   * never as "not a member" — the second would claim access does not exist when it does.
   */
  alreadyMember?: boolean;
}

/** What the action would do to the company's contact person, as the backend plans it. */
export type ContactPersonAction =
  | 'overwrite'
  | 'merge'
  | 'unchanged'
  | 'skipped';

export interface ContactPersonValues {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
}

export interface ContactPersonPlan {
  action: ContactPersonAction;
  /** Why nothing would be written, for `skipped`. */
  reason: string | null;
  ownerSelectionReason: OwnerSelectionReason | null;
  /** `company_details.contactPerson` as legacy holds it now. */
  current: ContactPersonValues;
  /** The owner as Master Data holds them. */
  proposed: ContactPersonValues;
  /** What legacy would hold after the run. */
  result: ContactPersonValues;
}

/** What the action would do to the company's address, as the backend plans it. */
export type AddressAction = 'overwrite' | 'unchanged' | 'skipped';

/** The five parts of `company_details.address` the backoffice edits and invoicing requires. */
export interface AddressValues {
  houseNumber: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  countryCode: string | null;
}

export interface AddressPlan {
  action: AddressAction;
  /** Why nothing would be written, for `skipped`. */
  reason: string | null;
  /** Parts Master Data does not hold, which an overwrite leaves empty. */
  missing: (keyof AddressValues)[];
  /** `company_details.address` as legacy holds it now. */
  current: AddressValues;
  /** The registered office as Master Data holds it, split into legacy's parts; null when it has none. */
  proposed: AddressValues | null;
  /** What legacy would hold after the run. */
  result: AddressValues;
}

export interface IncorporationDetails {
  company: IncorporationCompany;
  candidates: IncorporationCandidate[];
  /**
   * Everyone provisioning writes into legacy, not only the ones who can hold a portal account.
   *
   * `candidates` answers "who gets a login"; this answers "who ends up in the company". Optional,
   * so a backend deployed before the field leaves the section unrendered rather than showing an
   * empty one that reads as "nobody".
   */
  participants?: IncorporationParticipant[];
  ownerSelectionReason: OwnerSelectionReason | null;
  /**
   * The contact-person plan, shown before the press. Optional: a backend deployed before it sends
   * nothing, and the section then stays away rather than claiming nothing will change.
   */
  contactPerson?: ContactPersonPlan;
  /** The address plan, shown before the press. Optional for the same reason as `contactPerson`. */
  address?: AddressPlan;
  linkedPersonsWithoutRealmIdentity: number;
  canProvision: boolean;
  /**
   * Whether "Refresh from Master Data" can run: legacy holds this company, provisioned through this
   * page or under the same id, whatever its compliance case says. For the second kind the run
   * updates data only and grants no portal account. Optional and read as `=== true`: a backend
   * deployed before the field sends nothing, and the button then falls back to `isRerun`.
   */
  canRefresh?: boolean;
  warnings: string[];
}

export interface IncorporationList {
  total: number;
  page: number;
  perPage: number;
  records: IncorporationCompany[];
}

/**
 * A queue read, plus what the client had to do to get it.
 *
 * `truncated` is not part of the endpoint's contract — it says this read gave up at `MAX_PAGES`, so
 * `total` counts what was collected rather than what exists. It has to be shown: a filtered view
 * whose count silently understates the queue is the exact failure the filter is there to avoid.
 */
export interface IncorporationQueue extends IncorporationList {
  truncated?: boolean;
}

export interface ProvisionedMember {
  personId: string;
  keycloakId: string;
  /**
   * The local `users` row, once mirrored — null **only** on a `failed` member.
   *
   * The invariant is worth writing down, because the nullable type invites a guard that can never
   * fire: the backend resolves the realm user and the local row *before* it can report anything but
   * a failure, so every `granted` and `already_member` carries a real id (`grantMembership` spreads
   * `identified`). Anything reasoning about "did this person get an account" branches on `outcome`,
   * which is the field that says so.
   */
  userId: string | null;
  email: string | null;
  isOwner: boolean;
  outcome: MemberProvisioningOutcome;
  error: string | null;
  /**
   * Whether the `easybiz-client` realm role was granted — the Keycloak half of taking a company
   * into service.
   *
   * Independent of `outcome`: the membership lives in our database, the role in Keycloak, and they
   * fail separately. False on a `failed` member means it was never attempted (they are not a member
   * of this company); false on a granted one means the realm does not know they are a client yet,
   * which pressing the action again fixes.
   *
   * Optional on purpose, though the backend declares it required: this type describes what the wire
   * may actually carry, and a backend deployed before the field — or rolled back past it — sends
   * nothing. Read it as `=== false`, never as `!`.
   */
  clientRoleGranted?: boolean;

  /**
   * What Keycloak said when the role was refused, verbatim, or null when it said nothing.
   *
   * The three reasons a grant fails are fixed in three different places — the role is missing from
   * the realm, the service-account client may not read it, may not map it — and a bare
   * `clientRoleGranted: false` cannot tell them apart. The first real failure answered
   * `404 {"error":"Could not find role"}`, and that sentence reached nobody: it lived in a pod log
   * while the drawer said only that the role had not been granted.
   *
   * Optional for the same reason as the flag above: a backend deployed before the field sends
   * nothing, and the panel simply has less to say rather than rendering an empty line.
   */
  clientRoleError?: string | null;
}

/**
 * What one fill of a legacy company from Master Data moved.
 *
 * Counts rather than ids: what a CSM wants to know is whether the company now has its people and its
 * RCS extract, and a list of uuids answers a different question. `warnings` is where anything that
 * did not happen is said in words — including the one case that is not an error at all, a company
 * legacy already had, whose identity is deliberately left as legacy holds it.
 *
 * Every field is optional and read defensively for the same reason as `clientRoleGranted` on a
 * member: this type describes what the wire may carry, and a backend deployed before the field
 * sends nothing. Absent must read as "not stated", never as zero.
 */
export interface MdsFillResult {
  companyFieldsChanged?: string[];
  personsCreated?: number;
  personsRefreshed?: number;
  personsAttached?: number;
  personsSkipped?: number;
  rcsExtractLinked?: boolean;
  warnings?: string[];
}

export interface RefreshFromMdsResult {
  legacyCompanyId: string;
  filled: MdsFillResult;
  /**
   * Portal accounts granted by the refresh, one per person Master Data links.
   *
   * A director added after provisioning would otherwise reach legacy as a name and nothing else —
   * no membership and no `easybiz-client` role, both granted by the provisioning run that happened
   * before they existed. Optional for the usual reason.
   */
  members?: ProvisionedMember[];
}

export interface ProvisionResult {
  created: boolean;
  company: {
    id: string;
    privateId: number;
    schemaName: string;
    referenceId: string;
    name: string;
    status: string;
  };
  members: ProvisionedMember[];
  /** false with a company present is a partial success, not a failure — see `mdsError`. */
  mdsUpdated: boolean;
  mdsError: string | null;
  /**
   * What the fill from Master Data moved, on the run that provisioned the company.
   *
   * Optional for the usual reason — a backend deployed before it sends nothing — and reported apart
   * from the rest because it fails apart from the rest: the tenant and the memberships stand
   * whatever happens here.
   */
  filled?: MdsFillResult;
}

const ENDPOINT = `${env.VITE_ROUTE_API_BASE_URL || ''}/api/admin/mds_incorporations`;

export const INCORPORATIONS_QUERY_KEY = 'adminIncorporations';
export const INCORPORATION_DETAILS_QUERY_KEY = 'adminIncorporationDetails';

/**
 * The shared @api-client request interceptor rewrites `method` from a custom `httpMethod` field and
 * `data` from `requestBody` — the generated client always sets both. Without them `method` is
 * clobbered to `undefined` (the XHR adapter then throws before anything reaches the wire) and any
 * body passed the normal axios way is dropped, so a POST arrives as a bodyless GET.
 */
const requestConfig = (
  httpMethod: 'GET' | 'POST',
  options: { params?: Record<string, unknown>; body?: unknown } = {}
): AxiosRequestConfig =>
  ({
    httpMethod,
    params: options.params,
    requestBody: options.body,
  }) as AxiosRequestConfig;

/** MDS's fixed page size, used only if the backend ever answers without reporting one. */
const MDS_PAGE_SIZE = 40;

/**
 * How many pages `all` will walk before it stops.
 *
 * The loop is driven by a `total` the backend reports, so a wrong one would otherwise page against
 * Master Data indefinitely. Around a hundred companies sit in `incorporating` today — three pages —
 * and this is not the size the cap is set to guard.
 */
const MAX_PAGES = 25;

const fetchPage = async (
  page?: number,
  term?: string,
  provisioned?: boolean,
  changedOnly?: boolean
): Promise<IncorporationList> => {
  const response = await axios.get<IncorporationList>(
    ENDPOINT,
    requestConfig('GET', {
      // Left out entirely rather than sent empty: the backend validates `search` as a non-empty
      // string, so `?search=` would be a 400 on every keystroke that clears the box.
      params: {
        ...(page ? { page } : {}),
        ...(term ? { search: term } : {}),
        ...(provisioned ? { provisioned: true } : {}),
        // Only the opt-out travels: absent is the backend's own default, the changed-only work list.
        ...(provisioned && changedOnly === false ? { changedOnly: false } : {}),
      },
    })
  );

  return response.data;
};

/**
 * The whole queue, one page at a time.
 *
 * Only for the "Not provisioned" filter, which cannot be pushed down to where the paging happens:
 * whether legacy already holds a company is decided by *our* backend against the legacy database
 * after MDS has answered, and MDS's company list takes `page` and `search` and nothing else.
 *
 * Sequential rather than in parallel — each of these fans out to legacy lookups on the backend, and
 * there are three of them.
 */
const fetchEveryPage = async (term?: string): Promise<IncorporationQueue> => {
  const first = await fetchPage(1, term);
  const perPage = first.perPage || MDS_PAGE_SIZE;
  const wanted = Math.ceil(first.total / perPage);
  const pageCount = Math.min(wanted, MAX_PAGES);

  /*
   * Keyed by id because MDS orders by `-created_at`: a company created between two of these requests
   * shifts every row down a place, and the one on the boundary is then served twice. Left as-is that
   * is a duplicate React key on a table keyed by `mdsCompanyId`, and a count that overstates the
   * queue by however many times it happened.
   */
  const byId = new Map<string, IncorporationCompany>();
  const collect = (records: IncorporationCompany[]) => {
    records.forEach((record) => byId.set(record.mdsCompanyId, record));
  };

  collect(first.records);

  /*
   * The opposite direction has no such guard, and cannot cheaply have one: if a company *leaves*
   * `incorporating` mid-walk — another CSM provisions it, or the legacy sync flips it — every later
   * row moves up a place and whichever sat on a page boundary is served by neither request. Nothing
   * here can tell that from a queue that was always that length. A refresh shows it; the alternative
   * is a cursor MDS does not offer.
   */
  for (let page = 2; page <= pageCount; page += 1) {
    const next = await fetchPage(page, term);

    // A `total` that overstates what MDS will actually serve, rather than one that is merely large.
    if (next.records.length === 0) {
      break;
    }

    collect(next.records);
  }

  const records = [...byId.values()];

  return {
    total: records.length,
    page: 1,
    perPage,
    records,
    truncated: wanted > MAX_PAGES,
  };
};

/**
 * Companies Master Data still calls `incorporating` — the CSM provisioning queue.
 *
 * `all` reads every page instead of the one asked for, for the "Not provisioned" filter; the caller
 * then filters and pages the result itself. See `fetchEveryPage` for why that cannot be a query
 * parameter.
 *
 * TODO: replace with the generated `useAdminMdsIncorporationsController_list` once
 * easybiz-backend's Incorporations endpoints are deployed to stage, the operation is added to
 * `src/api-client/config/config-whitelist.ts` and the client is regenerated. This manual hook
 * exists so the page can be built ahead of that deploy; it mirrors the generated client's request
 * shape (Bearer token via the shared axios interceptors).
 */
export const useIncorporations = ({
  page,
  search,
  all,
  provisioned,
  changedOnly,
}: {
  page?: number;
  search?: string;
  all?: boolean;
  provisioned?: boolean;
  /** Provisioned view only: false lists every provisioned company, not only the changed ones. */
  changedOnly?: boolean;
} = {}) => {
  const term = search?.trim();

  return useQuery<IncorporationQueue>({
    // `page` is deliberately out of the key while `all` is set: the caller pages that result
    // locally, and keying on it would re-read the entire queue on every click of the paginator.
    // `provisioned` is in the key because it is a different list from the same endpoint.
    queryKey: [
      INCORPORATIONS_QUERY_KEY,
      all ? 'all' : (page ?? 1),
      term ?? '',
      provisioned
        ? changedOnly === false
          ? 'provisioned-all'
          : 'provisioned'
        : 'queue',
    ],
    queryFn: () =>
      all
        ? fetchEveryPage(term)
        : fetchPage(page, term, provisioned, changedOnly),
  });
};

/** One company, its linked people and everything worth reading before pressing Provision. */
export const useIncorporationDetails = (mdsCompanyId?: string | null) =>
  useQuery<IncorporationDetails>({
    queryKey: [INCORPORATION_DETAILS_QUERY_KEY, mdsCompanyId],
    queryFn: async () => {
      const response = await axios.get<IncorporationDetails>(
        `${ENDPOINT}/${mdsCompanyId}`,
        requestConfig('GET')
      );

      return response.data;
    },
    enabled: Boolean(mdsCompanyId),
  });

/**
 * Provision the company in legacy and move Master Data to in_service.
 *
 * Resolves — rather than rejecting — when only the MDS half failed: the tenant and its portal
 * accounts exist by then, and reporting that as an error would tell the CSM nothing happened.
 * The caller reads `mdsUpdated` and offers the retry.
 */
export const useProvisionIncorporation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ProvisionResult,
    unknown,
    {
      mdsCompanyId: string;
      incorporationDate?: string;
      updateContactPerson?: boolean;
      updateAddress?: boolean;
    }
  >({
    mutationFn: async ({
      mdsCompanyId,
      incorporationDate,
      updateContactPerson,
      updateAddress,
    }) => {
      const response = await axios.post<ProvisionResult>(
        `${ENDPOINT}/${mdsCompanyId}/provision`,
        // The body goes in `requestBody` above; anything passed here would be overwritten.
        undefined,
        requestConfig('POST', {
          body: {
            ...(incorporationDate ? { incorporationDate } : {}),
            ...(updateContactPerson === undefined
              ? {}
              : { updateContactPerson }),
            ...(updateAddress === undefined ? {} : { updateAddress }),
          },
        })
      );

      return response.data;
    },
    onSuccess: (_result, { mdsCompanyId }) => {
      void queryClient.invalidateQueries({
        queryKey: [INCORPORATIONS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCORPORATION_DETAILS_QUERY_KEY, mdsCompanyId],
      });
    },
    // Reported as well as rendered. The drawer already shows the reason inline, next to the button
    // that caused it, which is the right place for the CSM — but a refused provisioning is also an
    // operational event, and without this nobody outside that one screen ever learns it happened.
    onError: (error, { mdsCompanyId }) => {
      captureException(error, {
        tags: { feature: 'incorporations' },
        extra: { mdsCompanyId },
      });
    },
  });
};

/** The retry for the desync case: move MDS to in_service, touching nothing in legacy. */
export const useSyncMdsStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { mdsUpdated: boolean; incorporationStatus: string },
    unknown,
    { mdsCompanyId: string; incorporationDate?: string }
  >({
    mutationFn: async ({ mdsCompanyId, incorporationDate }) => {
      const response = await axios.post<{
        mdsUpdated: boolean;
        incorporationStatus: string;
      }>(
        `${ENDPOINT}/${mdsCompanyId}/mds_status`,
        undefined,
        requestConfig('POST', {
          body: incorporationDate ? { incorporationDate } : {},
        })
      );

      return response.data;
    },
    onSuccess: (_result, { mdsCompanyId }) => {
      void queryClient.invalidateQueries({
        queryKey: [INCORPORATIONS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCORPORATION_DETAILS_QUERY_KEY, mdsCompanyId],
      });
    },
    onError: (error, { mdsCompanyId }) => {
      captureException(error, {
        tags: { feature: 'incorporations' },
        extra: { mdsCompanyId },
      });
    },
  });
};

/**
 * Re-read this company from Master Data and fill legacy again.
 *
 * The action exists because the facts the Legal Docs Generator prints arrive *after* the button: a
 * company is provisioned the day it is incorporated, and its RCS number and RCS extract reach Master
 * Data days later. Additive — it inserts what legacy is missing, refreshes only the rows this
 * pipeline wrote, and never removes a participant.
 *
 * Resolves rather than rejecting when parts of the fill did not happen; the caller reads
 * `filled.warnings`, which name what to go and fix in Master Data before pressing it again.
 */
export const useRefreshFromMds = () => {
  const queryClient = useQueryClient();

  return useMutation<
    RefreshFromMdsResult,
    unknown,
    {
      mdsCompanyId: string;
      updateContactPerson?: boolean;
      updateAddress?: boolean;
    }
  >({
    mutationFn: async ({
      mdsCompanyId,
      updateContactPerson,
      updateAddress,
    }) => {
      const body = {
        ...(updateContactPerson === undefined ? {} : { updateContactPerson }),
        ...(updateAddress === undefined ? {} : { updateAddress }),
      };
      const response = await axios.post<RefreshFromMdsResult>(
        `${ENDPOINT}/${mdsCompanyId}/refresh`,
        undefined,
        Object.keys(body).length === 0
          ? requestConfig('POST')
          : requestConfig('POST', { body })
      );

      return response.data;
    },
    onSuccess: (_result, { mdsCompanyId }) => {
      void queryClient.invalidateQueries({
        queryKey: [INCORPORATION_DETAILS_QUERY_KEY, mdsCompanyId],
      });
      // The queue too, which a refresh did not use to change: the "Needs refresh" view keeps a
      // company only while Master Data is newer than the last fill, and this run just moved that
      // second timestamp. Without this the row a CSM has finished stays on their work list, with
      // nothing on screen to say it is done.
      void queryClient.invalidateQueries({
        queryKey: [INCORPORATIONS_QUERY_KEY],
      });
    },
    onError: (error, { mdsCompanyId }) => {
      captureException(error, {
        tags: { feature: 'incorporations' },
        extra: { mdsCompanyId },
      });
    },
  });
};
